import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { EyeIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { IdentityPlatformIcon, VideoTemplateIcon } from '@/shared/ui/Icons';
import { UnsavedChangesModal } from '@/shared/ui/UnsavedChangesModal';

import { RequiredFieldsLegend, SpaceVisibilitySection } from '../PersonaFormSections';
import { FORM_TABS } from './EditPersonaForm.schema';
import { EditPersonaFormProvider } from './context/EditPersonaFormProvider';
import { type UseEditPersonaFormOptions, useEditPersonaForm } from './hooks/useEditPersonaForm';
import { PersonaInformationTab, ProfileMediaTab } from './tabs';

interface EditPersonaFormProps {
  open: boolean;
  persona: UseEditPersonaFormOptions['persona'];
  /**
   * Persona-update async caller injected by the page. Audiences obtains it from
   * `useUpdatePersona().mutateAsync` and threads it down so this feature stays
   * cross-feature-OUT clean.
   */
  updatePersona: UseEditPersonaFormOptions['updatePersona'];
  onClose?: () => void;
  onExited?: () => void;
  onSuccess?: () => void;
}

/** Mirrors the 960px the design asks for, inset by 1rem on a narrow viewport. */
const DIALOG_WIDTH = 'w-[min(calc(100vw-2rem),60rem)]';

export function EditPersonaForm(props: EditPersonaFormProps) {
  const { t } = useTranslation('audiences');

  const { open, updatePersona, onClose, onExited, onSuccess } = props;
  const form = useEditPersonaForm({
    persona: props.persona,
    updatePersona,
    onSuccess: () => {
      onSuccess?.();
      onClose?.();
    },
    onClose,
  });

  const isLocked = form.isSubmitting || form.isProcessingMedia;
  const handleClose = isLocked ? undefined : form.handleClose;
  const isDefaultPersona = props.persona.type === 'Default';

  const saveDisabled = isDefaultPersona || !form.isDirty || isLocked;

  const saveButton = (
    <Button disabled={saveDisabled} onClick={() => void form.submitForm()}>
      {form.isSubmitting && <Spinner />}
      {t('audiences.editPersonaForm.saveChanges')}
    </Button>
  );

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) handleClose?.();
        }}
      >
        <DialogContent
          className={`${DIALOG_WIDTH} gap-0 p-0`}
          showCloseButton={!isLocked}
          // Keeps the edit dialog mounted but out of sight while the
          // unsaved-changes prompt is on screen, so no field state is lost.
          hidden={form.showUnsavedModal}
          onCloseComplete={onExited}
        >
          <DialogHeader className="border-b border-border px-6 py-4">
            <DialogTitle>{t('audiences.editPersonaForm.editPersona')}</DialogTitle>
          </DialogHeader>

          <EditPersonaFormProvider form={form}>
            <Tabs
              value={String(form.activeTabValue)}
              onValueChange={(next) => form.handleTabChange(Number(next))}
              className="min-h-0 gap-0"
            >
              <TabsList className="w-full border-b border-border">
                <TabsTrigger
                  value={String(FORM_TABS.personaInformation.value)}
                  className="flex-1"
                  disabled={isLocked}
                >
                  <IdentityPlatformIcon />
                  {FORM_TABS.personaInformation.label}
                </TabsTrigger>
                <TabsTrigger
                  value={String(FORM_TABS.profileMedia.value)}
                  className="flex-1"
                  disabled={isLocked}
                >
                  <VideoTemplateIcon />
                  {FORM_TABS.profileMedia.label}
                </TabsTrigger>
                <TabsTrigger
                  value={String(FORM_TABS.spaceVisibility.value)}
                  className="flex-1"
                  disabled={isLocked}
                >
                  <EyeIcon />
                  {FORM_TABS.spaceVisibility.label}
                </TabsTrigger>
              </TabsList>

              <DialogBody className="h-[75vh] overflow-y-auto px-6 py-8">
                <TabsContent value={String(FORM_TABS.personaInformation.value)}>
                  <PersonaInformationTab />
                </TabsContent>
                <TabsContent value={String(FORM_TABS.profileMedia.value)}>
                  <ProfileMediaTab />
                </TabsContent>
                <TabsContent value={String(FORM_TABS.spaceVisibility.value)}>
                  <SpaceVisibilitySection
                    disabled={!form.canEditSpaceVisibility}
                    includeCurrentWorkspace={form.includeCurrentWorkspaceOption}
                  />
                </TabsContent>
              </DialogBody>
            </Tabs>
          </EditPersonaFormProvider>

          <DialogFooter className="flex-row items-center justify-between border-t border-border px-6 py-4 sm:justify-between">
            <RequiredFieldsLegend />

            <div className="flex items-center gap-2">
              <Button variant="outline" disabled={isLocked} onClick={form.handleClose}>
                {t('audiences.editPersonaForm.cancel')}
              </Button>
              {isDefaultPersona ? (
                <TooltipProvider>
                  <Tooltip>
                    {/* A disabled button emits no pointer events, so the hoverable
                        target has to be the wrapper rather than the button. */}
                    <TooltipTrigger asChild>
                      <span>{saveButton}</span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t('audiences.editPersonaForm.defaultPersonasCannotBeEdited')}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                saveButton
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UnsavedChangesModal
        open={form.showUnsavedModal}
        onCancel={form.handleCancelLeave}
        onConfirmLeave={form.handleConfirmLeave}
      />
    </>
  );
}
